import React from "react";
import { useNavigate } from "react-router-dom";
const { useState, useEffect } = React;
const { ArrowRight } = {
  ArrowRight: () => <div>→</div>,
};

export const ServicesSection = () => {
  const navigate = useNavigate();
  const [activeService, setActiveService] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const services = [
    {
      id: "lifestyle",
      title: "Lifestyle Portraits",
      subtitle: "Authentic moments, naturally captured",
      description:
        "Capturing your genuine personality and connections in comfortable, natural settings that reflect who you truly are.",
      icon: "👤",
      color: "#548e9b",
      bgColor: "from-[#548e9b]/10 to-[#548e9b]/5",
      accentColor: "[#548e9b]",
      image:
        "https://res.cloudinary.com/shaszstudios-photography/image/upload/v1763243647/spring_family_session_ln76aq.jpg",
      link: "/lifestyle-portfolio",
      features: [
        "Natural and Studio lighting",
        "Candid moments",
        "Personal styling consultation",
        "Scenic outdoor locations",
      ],
    },
    {
      id: "maternity",
      title: "Maternity Sessions",
      subtitle: "Celebrating the beauty of motherhood",
      description:
        "Documenting this magical time with elegant, artistic imagery that honors the miracle of new life.",
      icon: "🤱",
      color: "#548e9b",
      bgColor: "from-[#548e9b]/10 to-[#548e9b]/5",
      accentColor: "[#548e9b]",
      image:
        "https://res.cloudinary.com/shaszstudios-photography/image/upload/v1763243691/maternity_couple_filmy_image_wqposl.jpg",
      link: "/maternity-portfolio",
      features: [
        "Studio & outdoor options",
        "Partner & family inclusion",
        "Wardrobe guidance",
        "Posing guidance",
      ],
    },
    {
      id: "newborn",
      title: "Newborn Sessions",
      subtitle: "Pure innocence, tenderly preserved",
      description:
        "Gentle, safe photography capturing your baby's first precious days with timeless, heartwarming portraits.",
      icon: "👶",
      color: "#548e9b",
      bgColor: "from-[#548e9b]/10 to-[#548e9b]/5",
      accentColor: "[#548e9b]",
      image:
        "https://res.cloudinary.com/shaszstudios-photography/image/upload/v1763247853/newborn_smiling_gnvqft.jpg",
      link: "/maternity-portfolio",
      features: [
        "Safety-first approach",
        "Comfortable studio environment at your home",
        "Props & styling included",
        "Family portraits",
      ],
    },
    {
      id: "weddings",
      title: "Weddings",
      subtitle: "Your love story, beautifully told",
      description:
        "Comprehensive wedding photography that captures every emotion, detail, and magical moment of your special day.",
      icon: "💍",
      color: "#548e9b",
      bgColor: "from-[#548e9b]/10 to-[#548e9b]/5",
      accentColor: "[#548e9b]",
      image:
        "https://res.cloudinary.com/shaszstudios-photography/image/upload/v1763411124/akshat_claudia_photo_1_ahpl5b.jpg",
      link: "/wedding-portfolio",
      features: [
        "Full day coverage",
        "Engagement session",
        "Online gallery",
        "Posing guidance",
      ],
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isHovered) {
        setActiveService((prev) => (prev + 1) % services.length);
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [isHovered, services.length]);

  return (
    <section
      id="services"
      className="py-24 bg-gradient-to-br from-slate-50 via-white to-teal-50/20 relative overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-teal-100/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-cyan-100/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-teal-50/10 to-emerald-50/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extralight text-gray-900 mb-6 tracking-tight">
            PHOTOGRAPHY{" "}
            <span className="italic text-teal-600 font-light">services</span>
          </h2>
          <div className="flex items-center justify-center mb-8">
            <div className="w-16 h-px bg-teal-500"></div>
            <div className="w-2 h-2 bg-teal-500 rounded-full mx-4"></div>
            <div className="w-16 h-px bg-teal-500"></div>
          </div>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
            Specialized in capturing life's most precious moments with artistry,
            emotion, and timeless elegance
          </p>
        </div>

        {/* Horizontal Services Layout */}  
        <div
          className="relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Main Service Display */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden mb-12 border border-gray-100/50">
            <div className="grid lg:grid-cols-2 min-h-[500px]">
              {/* Service Content */}
              <div className="p-12 lg:p-16 flex flex-col justify-center">
                <div className="mb-6">
                  <div className="text-4xl mb-4">
                    {services[activeService].icon}
                  </div>
                  <p className="text-xs tracking-widest uppercase text-teal-600 font-light mb-2">
                    {services[activeService].id}
                  </p>
                  <h3 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 tracking-wide">
                    {services[activeService].title}
                  </h3>
                  <p className="text-lg text-teal-600 font-light italic mb-6">
                    {services[activeService].subtitle}
                  </p>
                </div>

                <p className="text-gray-600 mb-8 font-light leading-relaxed text-lg">
                  {services[activeService].description}
                </p>

                <div className="mb-8">
                  <h4 className="text-sm font-light tracking-widest uppercase text-gray-800 mb-4">
                    What's Included
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {services[activeService].features.map((feature, idx) => (
                      <div
                        key={idx}
                        className="flex items-center text-sm text-gray-600 font-light"
                      >
                        <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-3"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>

                <button 
                  onClick={() => {
                    const link = services[activeService].link;
                    if (link) {
                      navigate(link);
                    }
                  }}
                  className="group flex items-center gap-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:from-teal-600 hover:to-teal-700 px-8 py-4 font-light tracking-wide transition-all duration-300 text-sm w-fit"
                >
                  LEARN MORE
                  <ArrowRight className="group-hover:translate-x-1 transition-transform w-4 h-4" />
                </button>
              </div>

              {/* Service Image */}
              <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 to-cyan-500/5"></div>
                <img
                  src={services[activeService].image}
                  alt={services[activeService].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
              </div>
            </div>
          </div>

          {/* Service Navigation */}
          <div className="flex justify-center space-x-2 mb-8">
            {services.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveService(index)}
                className={`transition-all duration-300 ${
                  index === activeService
                    ? "w-12 h-3 bg-teal-500 rounded-full"
                    : "w-3 h-3 bg-gray-300 rounded-full hover:bg-gray-400"
                }`}
              />
            ))}
          </div>

          {/* Service Thumbnails */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <button
                key={service.id}
                onClick={() => setActiveService(index)}
                className={`group relative overflow-hidden rounded-xl transition-all duration-500 transform hover:scale-105 ${
                  index === activeService
                    ? "ring-2 ring-teal-500 ring-offset-4 shadow-xl"
                    : "hover:shadow-lg"
                }`}
              >
                <div className="aspect-[4/3] relative flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-r from-teal-500 to-teal-600"></div>
                  {/* Mobile Layout - Icon + Title */}
                  <div className="md:hidden relative z-10 flex flex-col items-center justify-center text-white text-center px-2">
                    <div className="text-2xl mb-2">{service.icon}</div>
                    <span className="text-sm font-light leading-tight">
                      {service.title}
                    </span>
                  </div>
                  {/* Desktop Layout - Title centered */}
                  <span className="hidden md:block relative z-10 text-xl lg:text-2xl text-white text-center px-4">
                    {service.title}
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                  {/* Service Info Overlay - Desktop only (icon + subtitle) */}
                  <div className="hidden md:flex absolute bottom-0 left-0 right-0 p-4 text-white flex-col items-center text-center">
                    <div className="text-lg mb-1">{service.icon}</div>
                    <p className="text-xs opacity-90 font-light">
                      {service.subtitle}
                    </p>
                  </div>
                  {/* Active Indicator */}
                  {index === activeService && (
                    <div className="absolute top-4 right-4 w-3 h-3 bg-teal-400 rounded-full animate-pulse"></div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-20">
          <p className="text-gray-600 mb-8 font-light text-lg max-w-2xl mx-auto">
            Ready to capture your special moments? Let's discuss which service
            is perfect for your needs.
          </p>
          <button  onClick={() => navigate("/book-now")}  className="group flex items-center gap-3 mx-auto bg-transparent border-2 border-gray-300 text-gray-700 hover:border-teal-500 hover:text-teal-600 px-12 py-4 font-light tracking-wide transition-all duration-300 text-sm hover:shadow-lg">
            <span>BOOK CONSULTATION</span>
            <div className="w-4 h-4 border-r-2 border-t-2 border-current transform rotate-45 group-hover:translate-x-1 group-hover:scale-110 transition-all duration-300"></div>
          </button>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
